import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { FolderKanban, Mic, BarChart3, FileSearch, ShieldCheck, Sparkles, Check } from "lucide-react";

export const GUIDE_KEY = "meetiq_guide_seen_v1";

const steps = [
  { icon: FolderKanban, title: "Your private Workspace", body: "Create a project per client. Everything you create — projects, meetings, documents — is visible only to you. Start from the Workspace, then “New Project”." },
  { icon: Mic, title: "Capture the meeting", body: "Inside a meeting, record live or upload audio. Pick the spoken language (Telugu, Tamil, Hindi… or Auto) so the right engine is used. You can also paste a transcript." },
  { icon: ShieldCheck, title: "Transcript Quality Guard", body: "Every transcript is validated — repetition loops, wrong-language hallucinations and silence are flagged or excluded automatically, so nothing downstream is built on bad data." },
  { icon: BarChart3, title: "Insights & Minutes", body: "Switch to “Insights & Minutes” to see KPIs, the Meeting Health Score, topics, decisions, questions and issues — then generate a board-ready Minutes of Meeting (PDF/DOCX/XLSX)." },
  { icon: FileSearch, title: "RFQ Studio & Solution Design", body: "Turn tenders into branded proposals, and use the Solution Design tab to auto-draw logical & deployment architecture diagrams for each opportunity." },
];

export default function WelcomeGuide({ open, onOpenChange, isAdmin }) {
  const [i, setI] = useState(0);
  const items = isAdmin
    ? [...steps, { icon: Sparkles, title: "Admin oversight", body: "As admin you can see every user’s projects and meetings, and deactivate a user’s access instantly from the Users page." }]
    : steps;
  const last = i === items.length - 1;
  const step = items[i];
  const Icon = step.icon;

  const close = () => { localStorage.setItem(GUIDE_KEY, "1"); onOpenChange(false); setI(0); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="welcome-guide">
        <div className="brand-gradient text-white p-6">
          <Logo size={28} />
          <DialogTitle className="text-xl font-bold mt-4 text-white">Welcome to Yanthra Labs Business360</DialogTitle>
          <DialogDescription className="text-white/70 text-sm">Turn requirement meetings into board-ready deliverables — a quick tour.</DialogDescription>
        </div>
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 shrink-0 rounded-xl bg-[#29abe2]/15 text-[#1a7bb0] dark:text-[#5fc3f0] grid place-items-center"><Icon size={22} /></div>
            <div>
              <div className="text-xs font-semibold tracking-wider text-[#29abe2] uppercase">Step {i + 1} of {items.length}</div>
              <h3 className="text-lg font-semibold text-foreground mt-0.5">{step.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{step.body}</p>
            </div>
          </div>

          <div className="flex gap-1.5 mt-6">
            {items.map((_, idx) => <div key={idx} className={`h-1.5 rounded-full flex-1 transition-colors ${idx <= i ? "bg-[#29abe2]" : "bg-secondary"}`} />)}
          </div>

          <div className="flex items-center justify-between mt-6">
            <button onClick={close} className="text-sm text-muted-foreground hover:text-foreground" data-testid="guide-skip">Skip</button>
            <div className="flex gap-2">
              {i > 0 && <Button variant="outline" onClick={() => setI(i - 1)} data-testid="guide-back">Back</Button>}
              {last
                ? <Button onClick={close} className="gap-1.5" data-testid="guide-finish"><Check size={15} /> Get started</Button>
                : <Button onClick={() => setI(i + 1)} data-testid="guide-next">Next</Button>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
